import { useState } from 'react';
import type { OuterOrderItem, CartItemSummary, OrderSubItem } from '../../../types/analyticstypes';
import { formatMoney } from '../../../utils/analytics/format';
import KdsStationBadge from './KdsStationBadge';
import ImageWithSpinner from '../../content/imagewithspinner';

function SourceBadge({ source }: { source?: string }) {
  if (!source) return null;
  if (source === 'scan') {
    return (
      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] bg-purple-50 text-purple-700 border border-purple-200">
        <svg className="w-2.5 h-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M4 6v12M8 6v12M12 6v12M16 6v12M20 6v12" />
        </svg>
        Scanned
      </span>
    );
  }
  if (source === 'add_on') {
    return (
      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] bg-emerald-50 text-emerald-700 border border-emerald-200">
        <svg className="w-2.5 h-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
        </svg>
        Add-on
      </span>
    );
  }
  if (source.startsWith('upsell_')) {
    return (
      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] bg-fuchsia-50 text-fuchsia-700 border border-fuchsia-200">
        <svg className="w-2.5 h-2.5" fill="currentColor" viewBox="0 0 24 24">
          <path d="M3 9.5h18M5 9.5V19c0 1.1.9 2 2 2h10c1.1 0 2-.9 2-2V9.5M12 9.5V21M8 9.5C8 6 9.79 4 12 4s4 2 4 5.5" />
        </svg>
        Upsell
      </span>
    );
  }
  return null;
}

export default function CartItemRow({
  item,
  cartSummary,
  currency,
}: {
  item: OuterOrderItem;
  cartSummary?: CartItemSummary;
  currency: string;
}) {
  const [openSteps, setOpenSteps] = useState(false);
  const [openRecipes, setOpenRecipes] = useState<Record<string, boolean>>({});

  const isProduct = item.isProduct ?? !!item.upc;
  const isSignature = !!item.isSignatureItem;
  const source = cartSummary?.source;
  const isUpsell = source ? source.startsWith('upsell_') : false;

  const lineTotal = (item.price || 0) * (item.quantity || 1);
  const steps: OrderSubItem[] = item.steps || [];
  const addOns: OrderSubItem[] = item.addOns || [];

  return (
    <div className={`border rounded-lg p-3 ${isUpsell ? 'border-fuchsia-200 bg-fuchsia-50/30' : 'border-gray-200 bg-white'}`}>
      <div className="flex items-start gap-3">
        {item.image ? (
          <div className="w-12 h-12 shrink-0">
            <ImageWithSpinner imageUrl={item.image} />
          </div>
        ) : (
          <div className="w-12 h-12 rounded bg-gray-100 shrink-0 flex items-center justify-center text-gray-300 text-xs">—</div>
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <div className="flex items-center gap-1.5 flex-wrap">
                <span className="font-medium text-sm text-gray-900 truncate">{item.quantity}× {item.name}</span>
                {isSignature && (<span className="text-yellow-500" title="Signature item">★</span>)}
                <SourceBadge source={source} />
                {item.status && item.status !== 'COMPLETED' && (
                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 border border-amber-200">
                    {item.status}
                  </span>
                )}
              </div>
              <div className="text-[11px] text-gray-500 mt-0.5 flex flex-wrap gap-2">
                {isProduct && item.upc && <span>POS Code: {item.upc}</span>}
                {!isProduct && item.menuGuid && (
                  <span title={item.menuGuid}>menu: {item.menuGuid.slice(0, 8)}…</span>
                )}
                {item.calories > 0 && <span>{item.calories} cal</span>}
                {cartSummary?.suggestionId && (
                  <span className="text-fuchsia-700" title={cartSummary.suggestionId}>
                    suggest: {cartSummary.suggestionId.slice(0, 8)}…
                  </span>
                )}
              </div>
              {item.kdsInstructions && (
                <p className="text-[11px] italic text-gray-500 mt-0.5">{item.kdsInstructions}</p>
              )}
            </div>
            <div className="text-right shrink-0">
              <div className="font-mono text-sm font-medium text-gray-900">{formatMoney(lineTotal, currency)}</div>
              {item.quantity > 1 && (<div className="text-[10px] text-gray-400">{formatMoney(item.price, currency)} ea</div>)}
            </div>
          </div>

          {steps.length > 0 && (
            <div className="mt-2">
              <button
                onClick={() => setOpenSteps((v) => !v)}
                className="text-[11px] text-gray-600 hover:text-gray-900 font-medium flex items-center gap-1"
              >
                <svg
                  className={`w-3 h-3 transition-transform ${openSteps ? 'rotate-90' : ''}`}
                  fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5}
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                </svg>
                Customizations ({steps.length})
              </button>
              {openSteps && (
                <ul className="mt-1 space-y-1 ml-4">
                  {steps.map((s, i) => (
                    <li key={s.guid ?? i} className="text-[11px]">
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <span className="text-gray-700">{s.name}</span>
                          {s.kdsLocations.map((k, ki) => (
                            <KdsStationBadge key={ki} station={k.kdsLocation} status={k.status} />
                          ))}
                        </div>
                        {s.price > 0 && (
                          <span className="font-mono text-gray-500">+{formatMoney(s.price, currency)}</span>
                        )}
                      </div>
                      {s.recipes.length > 0 && (
                        <div className="ml-2 mt-0.5">
                          <button
                            onClick={() =>
                              setOpenRecipes((p) => ({
                                ...p,
                                [s.guid ?? String(i)]: !p[s.guid ?? String(i)],
                              }))
                            }
                            className="text-[10px] text-gray-400 hover:text-gray-600"
                          >
                            {openRecipes[s.guid ?? String(i)] ? '▾' : '▸'} {s.recipes.length} ingredient
                            {s.recipes.length !== 1 ? 's' : ''}
                          </button>
                          {openRecipes[s.guid ?? String(i)] && (
                            <ul className="ml-3 mt-0.5 space-y-0.5">
                              {s.recipes.map((r, ri) => (
                                <li key={ri} className="text-[10px] text-gray-500">
                                  {r.prepOrder}. {r.name} — {r.quantity} {r.measure}
                                  {r.fryer && (<span className="ml-1 text-orange-500" title="Fryer required">🍟</span>)}
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          {addOns.length > 0 && (
            <div className="mt-2 ml-2 border-l-2 border-emerald-200 pl-2">
              <p className="text-[10px] font-semibold text-emerald-700 uppercase tracking-wide mb-1">Add-ons</p>
              <ul className="space-y-1">
                {addOns.map((a, i) => (
                  <li key={a.guid ?? i} className="text-[11px] flex items-center justify-between gap-2">
                    <div className="flex items-center gap-1.5">
                      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] bg-emerald-50 text-emerald-700 border border-emerald-200">
                        Add-on
                      </span>
                      <span className="text-gray-700">{a.quantity}× {a.name}</span>
                      {a.kdsLocations.map((k, ki) => (
                        <KdsStationBadge key={ki} station={k.kdsLocation} status={k.status} />
                      ))}
                    </div>
                    <span className="font-mono text-gray-500">{formatMoney(a.price * a.quantity, currency)}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
